import React, { useState, useEffect, useRef } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8888";
const RATE_LIMIT_SECONDS = 15 * 60;

function PlaylistStatusBox({ onRefresh }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState("");

  // Poll every 5 seconds
  useEffect(() => {
    let isMounted = true;
    const fetchStatus = async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/playlist-status`);
        const data = await res.json();
        if (isMounted) setStatus(data);
      } catch (err) {
        if (isMounted) setError("Failed to load playlist status");
      }
    };
    fetchStatus();
    const interval = setInterval(fetchStatus, 5000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Expose refresh function to parent
  useEffect(() => {
    if (onRefresh) {
      onRefresh(() => {
        fetch(`${BACKEND_URL}/playlist-status`)
          .then(res => res.json())
          .then(data => setStatus(data))
          .catch(err => setError("Failed to load playlist status"));
      });
    }
  }, [onRefresh]);

  if (error) return <div className="text-red-400">{error}</div>;
  if (!status) return <div className="text-gray-400">Loading playlist...</div>;

  const current = status.currently_playing && status.currently_playing.item;
  const queue = status.queue && status.queue.queue ? status.queue.queue : [];

  return (
    <div className="bg-gray-800 rounded-lg p-4 w-72 min-w-[16rem] mb-6 shadow-lg">
      <h2 className="text-lg font-bold mb-3 text-green-300">Current Playlist</h2>
      {current ? (
        <div className="mb-4 flex items-center">
          {current.album?.images?.[2]?.url && (
            <img src={current.album.images[2].url} alt="album art" className="w-12 h-12 rounded mr-3" />
          )}
          <div>
            <div className="font-semibold">{current.name}</div>
            <div className="text-sm text-gray-400">{current.artists.map(a => a.name).join(", ")}</div>
            <div className="text-xs text-yellow-300 mt-1">Now Playing</div>
          </div>
        </div>
      ) : (
        <div className="text-gray-400 mb-4">Nothing is playing</div>
      )}
      <div className="text-sm text-gray-300 mb-1 font-semibold">Up Next:</div>
      {queue.length === 0 && <div className="text-gray-500 text-xs">Queue is empty</div>}
      <ul>
        {queue.map((track, idx) => (
          <li key={track.id || idx} className="flex items-center mb-2">
            {track.album?.images?.[2]?.url && (
              <img src={track.album.images[2].url} alt="album art" className="w-8 h-8 rounded mr-2" />
            )}
            <div>
              <div className="font-medium text-xs">{track.name}</div>
              <div className="text-xs text-gray-400">{track.artists.map(a => a.name).join(", ")}</div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SearchBar({ onResults, setError, setLoading }) {
  const [query, setQuery] = useState("");
  const debounceRef = useRef();
  const inputRef = useRef();

  // Debounced real-time search
  useEffect(() => {
    if (query.length >= 3) {
      setLoading(true);
      setError("");
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        fetchResults();
      }, 400);
    } else {
      onResults([]);
    }
    // eslint-disable-next-line
  }, [query]);

  const fetchResults = async () => {
    if (query.length < 3) return;
    setLoading(true);
    setError("");
    onResults([]);
    try {
      const res = await fetch(
        `${BACKEND_URL}/search?q=${encodeURIComponent(query)}&type=track`
      );
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      onResults(data.tracks ? data.tracks.items : []);
    } catch (err) {
      setError("Search failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Only submit on Enter or button click
  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    fetchResults();
  };

  return (
    <form
      onSubmit={handleSearch}
      className="flex w-full max-w-xl mb-6 justify-center"
      autoComplete="off"
    >
      <input
        type="text"
        className="flex-1 p-3 rounded-l bg-gray-800 border-none focus:outline-none"
        placeholder="Search for a song..."
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        disabled={false}
        ref={inputRef}
      />
      <button
        type="submit"
        className="p-3 bg-green-600 hover:bg-green-700 rounded-r font-semibold"
        disabled={query.length < 3}
      >
        Search
      </button>
    </form>
  );
}

function App() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [rateLimit, setRateLimit] = useState(0);
  const [successMsg, setSuccessMsg] = useState("");
  const [hasVIPAccess, setHasVIPAccess] = useState(false);
  const [refreshPlaylist, setRefreshPlaylist] = useState(null);

  // Check for VIP access on mount
  useEffect(() => {
    const vipExpiry = localStorage.getItem('vipAccessExpiry');
    if (vipExpiry && Date.now() < parseInt(vipExpiry, 10)) {
      setHasVIPAccess(true);
    }
  }, []);

  // On mount, check for rate limit
  useEffect(() => {
    const lastAdd = localStorage.getItem("lastAddToQueue");
    if (lastAdd) {
      const elapsed = Math.floor((Date.now() - parseInt(lastAdd, 10)) / 1000);
      if (elapsed < RATE_LIMIT_SECONDS) {
        setRateLimit(RATE_LIMIT_SECONDS - elapsed);
      }
    }
  }, []);

  // Countdown timer
  useEffect(() => {
    if (rateLimit > 0) {
      const timer = setInterval(() => {
        setRateLimit((r) => (r > 0 ? r - 1 : 0));
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [rateLimit]);

  const handleAddToQueue = async (trackUri) => {
    setError("");
    setSuccessMsg("");
    try {
      const res = await fetch(`${BACKEND_URL}/add-to-queue`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ trackUri }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.waitSeconds) setRateLimit(data.waitSeconds);
        throw new Error(data.error || "Failed to add to queue");
      }
      localStorage.setItem("lastAddToQueue", Date.now().toString());
      setRateLimit(RATE_LIMIT_SECONDS);
      setSuccessMsg("Song added to queue!");
      // Immediately refresh the playlist to show the new song
      if (refreshPlaylist) {
        setTimeout(refreshPlaylist, 1000); // Small delay to ensure Spotify API has updated
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleGrantAccess = async () => {
    const password = prompt('Enter Access Code:');
    if (!password) return;

    try {
      const res = await fetch(`${BACKEND_URL}/grant-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      if (res.ok) {
        alert('Access granted for 6 hours!');
        // Set local storage flag to remember access
        const sixHours = 6 * 60 * 60 * 1000;
        localStorage.setItem('vipAccessExpiry', (Date.now() + sixHours).toString());
        setHasVIPAccess(true);
        // Clear any existing rate limit timer
        setRateLimit(0);
      } else {
        alert('Invalid password.');
      }
    } catch (err) {
      alert('An error occurred.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white flex flex-col min-h-screen">
      {/* Top section: logo and search bar, always at the top and centered */}
      <div className="flex flex-col items-center pt-8 pb-2">
        <img
          src={process.env.PUBLIC_URL + "/KC-logo.png"}
          alt="Kellerclub Logo"
          className="w-24 h-24 rounded-full object-cover mb-4 shadow-lg border-4 border-white"
          style={{ background: '#fff' }}
        />
        <h1 className="text-3xl font-bold mb-6 mt-2">Kellerclub Song Queue</h1>
      </div>
      {/* Main content area: playlist and search/results aligned horizontally */}
      <div className="flex flex-1 flex-col md:flex-row w-full max-w-6xl mx-auto items-start justify-start">
        {/* Playlist box on the left, pushed further left on desktop */}
        <div className="md:w-80 md:mr-16 mb-8 md:mb-0 flex-shrink-0 md:-ml-16">
          <PlaylistStatusBox onRefresh={setRefreshPlaylist} />
        </div>
        {/* Main content (search/results) always horizontally centered */}
        <div className="flex-1 flex flex-col items-center justify-start w-full mx-auto">
          <SearchBar onResults={setResults} setError={setError} setLoading={setLoading} />
          {/* Error, success, and rate limit messages OUTSIDE the form to avoid input remounts */}
          {error && <div className="text-red-400 mb-4">{error}</div>}
          {successMsg && <div className="text-green-400 mb-4">{successMsg}</div>}
          {rateLimit > 0 && (
            <div className="mb-4 text-yellow-300">
              You can add another song in {Math.floor(rateLimit / 60)}:
              {(rateLimit % 60).toString().padStart(2, "0")} minutes
            </div>
          )}
          <div className="w-full max-w-xl">
            {results.length > 0 && (
              <ul>
                {results.map((track) => (
                  <li
                    key={track.id}
                    className="flex items-center justify-between bg-gray-800 rounded p-3 mb-3"
                  >
                    <div className="flex items-center">
                      {track.album?.images?.[2]?.url && (
                        <img
                          src={track.album.images[2].url}
                          alt="album art"
                          className="w-12 h-12 rounded mr-3"
                        />
                      )}
                      <div>
                        <div className="font-semibold">{track.name}</div>
                        <div className="text-sm text-gray-400">
                          {track.artists.map((a) => a.name).join(", ")}
                        </div>
                      </div>
                    </div>
                    <button
                      className="ml-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded text-white font-semibold disabled:opacity-50"
                      onClick={() => handleAddToQueue(track.uri)}
                      disabled={!hasVIPAccess && rateLimit > 0}
                    >
                      Add to Queue
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      <footer className="mt-10 text-gray-500 text-xs w-full text-center pb-4 md:pb-6 md:mt-0">
        Powered by Spotify | Kellerclub | v0.1.2<br />
        <button
          onClick={handleGrantAccess}
          className="opacity-10 hover:opacity-100 transition-opacity duration-300 text-xs ml-4 p-1 rounded"
          title="Grant Access"
        >
          Grant Access
        </button>
      </footer>
    </div>
  );
}

export default App;
